import { useRef } from "react";
import { addDays, getDay } from "date-fns";
import { useFetchCustomerTimezone } from "reports/utils/customerInfoUtils";
import { NUM_DAYS_IN_WEEK, ONE_WEEK_OF_MS } from "reports/utils/timeUtils";
import PnlBreakdownGetter from "../../../../getters/pnlBreakdownGetter/PnlBreakdownGetter";
import { getEpochTime } from "../bespokeReportUtils";

import { lastNWeekDataAtom } from "reports/store/store";
import { useAtom } from "jotai";
import { PnlBreakdown } from "../../../../getters/pnlBreakdownGetter/pnlBreakdownTypes";

export type LastNWeekGetterProps = {
  ge?: number;
  lt?: number;
  startDayOfWeek?: number;
  endDayOfWeek?: number;
  startHour?: number;
  startMinute?: number;
  endHour?: number;
  endMinute?: number;
  numWeeks?: number;
  machemetryIds: string[];
  setStateLastNWeeksData?: React.Dispatch<React.SetStateAction<PnlBreakdown[]>>;
  shouldSkipQuery?: boolean;
  shouldFilterByMachemetryId?: boolean;
};

export default function LastNWeekGetter({
  ge,
  lt,
  startDayOfWeek = 1, // if not included, set it to Monday
  endDayOfWeek, // if not included, It will be set to 7 days after start of the Day
  // (example : startDayof Week is Monday then endDayofWeek would be the Monday of the next week)
  startHour = 0,
  startMinute = 0,
  endHour, // if not included, same as startHour
  endMinute, // if not included same as startMinute
  numWeeks = 10,
  machemetryIds,
  shouldSkipQuery = false,
  shouldFilterByMachemetryId = false,
  setStateLastNWeeksData,
}: LastNWeekGetterProps) {
  const timeZone = useFetchCustomerTimezone();
  const now = useRef(new Date()).current;
  const [, setLastNWeekData] = useAtom(lastNWeekDataAtom);

  if (timeZone) {
    const lastNWeekTimeRanges = getLastNWeekTimeRanges({
      ge,
      lt,
      now,
      startDayOfWeek,
      startHour,
      startMinute,
      endHour,
      endMinute,
      timeZone,
      endDayOfWeek,
      numWeeks,
    });
    return (
      <>
        {lastNWeekTimeRanges.map(({ ge, lt }, index) => {
          return (
            <PnlBreakdownGetter
              key={ge}
              range={{ ge, lt }}
              machemetryIds={machemetryIds}
              setPnlBreakdownData={setStateLastNWeeksData ?? setLastNWeekData}
              multiData={true}
              shouldFilterByMachemetryId={shouldFilterByMachemetryId}
              shouldSkipQuery={shouldSkipQuery}
            />
          );
        })}
      </>
    );
  }

  return <></>;
}

export function getLastNWeekTimeRanges({
  ge,
  lt,
  now,
  startDayOfWeek,
  startHour,
  startMinute,
  endHour,
  endMinute,
  timeZone,
  endDayOfWeek,
  numWeeks,
}: {
  ge?: number;
  lt?: number;
  now: Date;
  startDayOfWeek: number;
  startHour: number;
  startMinute: number;
  endHour?: number;
  endMinute?: number;
  timeZone: string;
  endDayOfWeek?: number;
  numWeeks: number;
}) {
  const currentDay = getDay(now);

  // calculate the start date based on the startDayOfWeek (0-Sun, 1-Mon, 2-Tue, 3-Wed, 4-Thurs,5-Fri, 6-Sat)
  const weekStartDate =
    currentDay === startDayOfWeek
      ? now
      : addDays(now, startDayOfWeek - currentDay);

  // set the start time to the hour and minute (tenant's time zone)
  let weekStartTime = ge
    ? ge + ONE_WEEK_OF_MS
    : getEpochTime({
        hour: startHour,
        minute: startMinute,
        timeZone,
        baselineDate: weekStartDate,
      });

  const isEndDayBeforeStartDay = endDayOfWeek
    ? startDayOfWeek > endDayOfWeek
    : false;

  const isSunday = endDayOfWeek === 0;

  const numDaysToAdd = endDayOfWeek
    ? endDayOfWeek -
      startDayOfWeek +
      // if endDayOfWeek is included and is less than the start day of the week, add 7 days so that the endDate will be in nextWeek
      (isEndDayBeforeStartDay ? NUM_DAYS_IN_WEEK : 0)
    : NUM_DAYS_IN_WEEK - (isSunday ? startDayOfWeek : 0);

  // calculate the end date based on the endDayOfWeek (0-Sun, 1-Mon, 2-Tue, 3-Wed, 4-Thurs,5-Fri, 6-Sat)
  const weekEndDate =
    currentDay === endDayOfWeek ? now : addDays(weekStartDate, numDaysToAdd);

  // set the end Time to the hour and minute (tenant's time zone)
  let weekEndTime = lt
    ? lt + ONE_WEEK_OF_MS
    : getEpochTime({
        hour: endHour ?? startHour,
        minute: endMinute ?? startMinute,
        timeZone,
        baselineDate: weekEndDate,
      });

  // If the report's first elem in lastNWeekTimeRanges (end time - one week)
  // is in the future, we need to go to the previous week
  if (weekEndTime - ONE_WEEK_OF_MS > now.getTime()) {
    weekEndTime -= ONE_WEEK_OF_MS;
    weekStartTime -= ONE_WEEK_OF_MS;
  }

  // calculate the past N weeks ge and lt values
  const lastNWeekTimeRanges = [...Array(numWeeks)].map((_, index) => {
    const ge = weekStartTime - (index + 1) * ONE_WEEK_OF_MS;
    const lt = weekEndTime - (index + 1) * ONE_WEEK_OF_MS;
    return { ge, lt };
  });

  return lastNWeekTimeRanges;
}
